import { type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';

/**
 * Một nút ở đầu trang sổ — APM-DSX-e. Trang dựng **một mảng** các nút này
 * (`DecisionSheetPage`, khối `actions`) thay vì một chuỗi JSX có điều kiện:
 * thêm nút mới (Chia sẻ, Giao việc trả lời…) là thêm một phần tử, không phải
 * chen vào giữa một khối điều kiện lồng nhau.
 */
export interface SheetAction {
  /** Khoá React, và `data-testid="decision-sheet-action-<id>"`. */
  readonly id: string;
  readonly label: string;
  readonly icon?: LucideIcon | undefined;
  readonly onClick: () => void;
  readonly variant?: 'default' | 'outline' | 'ghost' | undefined;
  readonly disabled?: boolean | undefined;
  /** Hành động phá huỷ (Xoá sổ) — chữ đỏ, để người đọc thấy trước khi bấm. */
  readonly danger?: boolean | undefined;
  /** Lý do khi `disabled`, hoặc gợi ý. */
  readonly title?: string | undefined;
}

/** Hàng nút đầu trang — gói dòng ở màn hẹp, vùng bấm 40px trên điện thoại. */
export function SheetActionBar({
  actions,
}: {
  readonly actions: readonly SheetAction[];
}): ReactNode {
  if (actions.length === 0) return null;
  return (
    <span className="ml-auto flex flex-wrap gap-2" data-testid="decision-sheet-actions">
      {actions.map(
        ({ id, label, icon: Icon, onClick, variant = 'outline', disabled, danger, title }) => (
          <Button
            key={id}
            size="sm"
            variant={variant}
            disabled={disabled}
            title={title}
            onClick={onClick}
            className={cn('max-sm:h-10', danger && 'text-destructive hover:text-destructive')}
            data-testid={`decision-sheet-action-${id}`}
          >
            {Icon ? <Icon aria-hidden /> : null}
            {label}
          </Button>
        ),
      )}
    </span>
  );
}
